import React, { useEffect } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import EditableTitle from "../../App/Common/Components/EditableTitle";
import { IClientPatch, IPatchAction } from "../../components/course-author/helpers/patches";
import { patchPayloadCreator } from "../../components/course-author/helpers/patches/creators";
import { H1 } from "../../components/utils/Headings";
import SaveInProgress, { ISaveInProgressStateProps } from "../../components/utils/SaveInProgress";
import { SideMenu } from "../../components/utils/SideMenu/SideMenu.container";
import { SideMenuArrowContext, SideMenuCloseArrow } from "../../components/utils/SideMenu/SideMenuCloseArrow";
import { useTheme } from "../../components/utils/Theme";
import Spinner from "../../components/utils/Spinner";
import SubTopbar from "../../components/utils/SubTopbar";
import TopBar from "../../components/utils/TopBar";
import * as selectors from "../../selectors/selectors";
import { getThemeColorsFromLessString } from "../../utils/theme";
import { IState } from "../reducer";
import { ICompleteOrganization, RequestState } from "../types/common";
import * as actions from "./actions";
import Apply from "./components/Apply";
import CustomStyle from "./components/CustomStyle";
import Fonts from "./components/Fonts";
import YourLogo from "./components/YourLogo";
import { applyThemeToOrganization, modifyLessCodePatch, modifyStyleTitlePatch } from "./helpers/patches";
import { IStyle } from "../../Types/Style";

interface IThemeManagementStateProps {
  accountId: string;
  firstFetchStatus: RequestState;
  organization: ICompleteOrganization | null;
  organizationName: string;
  style: IStyle | null;
  currentStyle?: IStyle | null;
  userName: string;
}

type ThemeManagementStateProps = IThemeManagementStateProps & ISaveInProgressStateProps;

interface IThemeManagementDispatchProps {
  mount: (styleId: string) => void;
  patchStyle: (versionId: number, patches: IClientPatch<IPatchAction>, location: actions.StyleLocation) => void;
  patchOrganization: (versionId: number, patches: IClientPatch<IPatchAction>) => void;
  unmount: () => void;
}

type ThemeManagementProps = ThemeManagementStateProps &
  IThemeManagementDispatchProps &
  RouteComponentProps<{ styleId: string }>;

interface IThemeComponentProps {
  style: IStyle;
  organization: ICompleteOrganization;
  applyThemeToOrg: (styleId: string, organizationId: string) => void;
  modifyTitle: (title: string) => void;
  modifyLessCode: (lessCode: string) => void;
}

const ThemeComponent: React.FC<IThemeComponentProps> = props => {
  const modifyTitle = (newTitle: string) => {
    if (newTitle !== props.style.displayName) {
      props.modifyTitle(newTitle);
    }
  };
  const modifyLessCode = (lessCode: string) => {
    if (lessCode !== props.style.lessCode) {
      props.modifyLessCode(lessCode);
    }
  };
  const applyThemeToOrg = (styleId: string) => {
    if (styleId !== props.organization.style) {
      props.applyThemeToOrg(styleId, props.organization.organizationId);
    }
  };

  return (
    <>
      <H1>
        <EditableTitle
          addTitle={modifyTitle}
          modifyTitle={modifyTitle}
          currentTitle={props.style.displayName}
          defaultTitle={"Untitled Theme"}
          isMainTitle={true}
        />
      </H1>
      <YourLogo style={props.style} />
      <CustomStyle style={props.style} modifyLessCode={modifyLessCode} />
      <Fonts style={props.style} />
      <Apply style={props.style} applyStyleToCurrentOrg={applyThemeToOrg} organization={props.organization} />
    </>
  );
};

export const ThemeManagement: React.FC<ThemeManagementProps> = props => {
  const [menuExpanded, setMenuExpanded] = React.useState(true);
  const themeContext = useTheme();
  const { isOpen } = React.useContext(SideMenuArrowContext);
  const toggleMenu = () => setMenuExpanded(!menuExpanded);
  const { userName, organizationName } = props;
  const renderPage = (children: React.ReactNode) => (
    <>
      <SaveInProgress firstFetch={props.firstFetch} inProgress={props.inProgress} />
      <TopBar showToggleMenu={true} onToggleMenuClick={toggleMenu} userName={userName} />
      <SideMenuCloseArrow />
      <SideMenu
        expanded={menuExpanded}
        organizationName={organizationName}
        userName={userName}
        onClose={toggleMenu}
        isOpen={isOpen}
      />
      <SubTopbar withSidebar={true} isOpen={isOpen}>
        {children}
      </SubTopbar>
    </>
  );

  const setSideMenuTabAsSelected = () => {
    const element = document.getElementById("theme-link");
    element?.setAttribute("class", "selected");
  };

  useEffect(setSideMenuTabAsSelected, [props.firstFetchStatus]);

  useEffect(() => {
    props.mount(props.match.params.styleId);
    return props.unmount;
  }, [props.match.params.styleId]);

  if (props.firstFetchStatus === RequestState.FAILED) {
    return renderPage(<div>There was an error fetching the theme's information. Please, try again later.</div>);
  }

  if (!props.style || !props.organization) {
    return <Spinner data-testid="spinner" />;
  }

  const { organization, style } = props;
  const modifyTitle = (newTitle: string) => {
    const patch = modifyStyleTitlePatch(style, props.accountId, newTitle);
    props.patchStyle(style.versionId, patch, "Title");
  };
  const modifyLessCode = (lessCode: string) => {
    const patch = modifyLessCodePatch(style, props.accountId, lessCode);
    props.patchStyle(style.versionId, patch, "LessCode");
    if (organization.style === style.styleId) {
      themeContext.changeColors(getThemeColorsFromLessString(patch.patch.action.y));
      return;
    }
    confirm("Colours saved successfully. Please press the Apply Theme button to apply colours.");
  };
  const applyThemeToOrg = (styleId: string) => {
    const patch = applyThemeToOrganization(organization, props.accountId, styleId);
    props.patchOrganization(organization.versionId, patch);
    themeContext.changeColors(getThemeColorsFromLessString(style.lessCode));
    themeContext.changeColors(getThemeColorsFromLessString(style.lessCode));
  };

  return renderPage(
    <ThemeComponent
      style={props.style}
      organization={props.organization}
      applyThemeToOrg={applyThemeToOrg}
      modifyTitle={modifyTitle}
      modifyLessCode={modifyLessCode}
    />
  );
};

const mapStateToProps = (state: IState): ThemeManagementStateProps => ({
  accountId: selectors.accountId(state),
  currentStyle: state.App._.organization?.style,
  firstFetch: state.App.ThemeManagement.style === null,
  firstFetchStatus: state.App.ThemeManagement.firstFetchStatus,
  inProgress:
    state.App.ThemeManagement.patchStyleStatus === RequestState.PENDING ||
    state.App.ThemeManagement.addNewLogoStatus === RequestState.PENDING,
  organization: state.App.ThemeManagement.organization,
  organizationName: selectors.organizationName(state),
  style: state.App.ThemeManagement.style,
  userName: selectors.accountName(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IAction>): IThemeManagementDispatchProps => ({
  mount: (styleId: string) => dispatch(actions.mount(styleId)),
  patchOrganization: (versionId: number, patch: IClientPatch<IPatchAction>) =>
    dispatch(actions.patchOrganization(patchPayloadCreator(versionId, patch))),
  patchStyle: (versionId: number, patch: IClientPatch<IPatchAction>, location: actions.StyleLocation = null) =>
    dispatch(
      actions.patchStyle({
        location,
        patch: patchPayloadCreator(versionId, patch),
      })
    ),
  unmount: () => dispatch(actions.unmount()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThemeManagement);
